import { createStyles } from "@mantine/core";

// Ports the notes-redesign demo's single-note "PDF Preview" template
// (.pdf-lh/.pdf-title/.pdf-meta/.pdf-h/.pdf-body/.pdf-sign/.pdf-foot) 1:1.
const useStyles = createStyles(() => ({
  footerBar: {
    flex: "none",
    padding: "16px 24px",
    borderTop: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
  },
  cancelBtn: {
    padding: "12px 20px",
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    height: 44,
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "var(--app-text)",
    ":hover": { backgroundColor: "var(--app-surface)", opacity: 0.8 },
  },
  downloadBtn: {
    padding: "12px 20px",
    borderRadius: 8,
    border: "1px solid var(--app-primary)",
    backgroundColor: "var(--app-primary)",
    height: 44,
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "var(--app-on-primary)",
    ":hover": { backgroundColor: "var(--app-primary)", opacity: 0.8 },
  },
  page: {
    width: "100%",
    background: "var(--app-surface, #fff)",
    border: "1px solid var(--app-border)",
    boxShadow: "0 6px 30px rgba(16,24,40,.14)",
    borderRadius: 12,
    padding: "40px 44px",
    color: "var(--app-text, #141414)",
  },
  letterhead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    borderBottom: "2px solid #141414",
    paddingBottom: 14,
    marginBottom: 20,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "var(--app-primary)",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    color: "#000",
    fontSize: 19,
    flex: "none",
  },
  logoImg: {
    width: 40,
    height: 40,
    borderRadius: 10,
    objectFit: "cover",
    flex: "none",
  },
  org: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "var(--app-text, #141414)",
  },
  lhRight: {
    textAlign: "right",
    fontSize: 11,
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
  },
  title: {
    fontSize: 21,
    fontWeight: 700,
    margin: 0,
    color: "var(--app-text, #141414)",
  },
  meta: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 24px",
    margin: "18px 0",
    padding: "16px 0",
    borderTop: "1px solid #EAEAEA",
    borderBottom: "1px solid #EAEAEA",
  },
  metaK: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    fontWeight: 700,
  },
  metaV: {
    fontSize: 13.5,
    color: "var(--app-text, #141414)",
    marginTop: 3,
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#101828",
    margin: "26px 0 10px",
    paddingBottom: 8,
    borderBottom: "1px solid #EAEAEA",
  },
  // Studio template grid — mirrors the note-builder / create-form layout.
  tplCard: {
    border: "1px solid #E7E9EE",
    borderRadius: 14,
    padding: "16px 18px 18px",
    margin: "18px 0",
  },
  tplCardHead: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#101828",
    marginBottom: 14,
  },
  tplGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: "14px 20px",
  },
  tplFieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#101828",
    marginBottom: 6,
  },
  tplFieldValue: {
    minHeight: 40,
    border: "1px solid #E7E9EE",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13.5,
    color: "#2b2b2b",
    background: "#FAFBFC",
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
  },
  tplFieldHelp: {
    fontSize: 11.5,
    color: "var(--app-text-faint, var(--app-text-muted))",
    marginTop: 5,
  },
  sectionLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    fontWeight: 700,
    margin: "22px 0 7px",
  },
  sectionBody: {
    fontSize: 13.5,
    lineHeight: 1.65,
    color: "#2b2b2b",
    whiteSpace: "pre-wrap",
  },
  signWrap: {
    marginTop: 34,
    paddingTop: 16,
    borderTop: "1px solid #EAEAEA",
  },
  signLbl: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--app-text-faint, var(--app-text-muted))",
    fontWeight: 700,
    marginBottom: 6,
  },
  sig: {
    fontFamily: "'Segoe Script','Snell Roundhand','Brush Script MT',cursive",
    fontSize: 24,
    color: "#141414",
    margin: "2px 0 4px",
  },
  sigImg: {
    display: "block",
    maxHeight: 40,
    maxWidth: 160,
    objectFit: "contain",
    margin: "4px 0",
  },
  sigSm: {
    fontSize: 11,
    color: "var(--app-text-muted)",
  },
  footer: {
    marginTop: 34,
    paddingTop: 12,
    borderTop: "1px solid #EAEAEA",
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10.5,
    color: "var(--app-text-faint, var(--app-text-muted))",
  },
}));

export default useStyles;
